/**
 * <FeaturedPulseStrip /> — auto-rotating hero strip of admin-featured
 * Neighborhood Pulse posts. Sits on the homepage. Silently hides when
 * there are no featured posts, so the homepage never renders an empty
 * placeholder.
 *
 * Uses the same auto-advance cadence (8s) as WorldChangerOfTheDay for a
 * consistent editorial rhythm.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Star, ArrowUpRight, MapPin, ChevronLeft, ChevronRight, Radio, Calendar } from "lucide-react";
import Avatar from "@/components/Avatar";

const ADVANCE_MS = 8000;

const CARD_TILE = {
  event: { color: "#B25D38", Icon: Calendar },
  news:  { color: "#0891B2", Icon: Radio },
  info:  { color: "#059669", Icon: Radio },
};
function tileFor(item) {
  if (item?.category && CARD_TILE[item.category]) return CARD_TILE[item.category];
  const tags = (item?.tags || []).map((t) => (t || "").toLowerCase());
  if (tags.includes("events")) return CARD_TILE.event;
  if (tags.includes("news")) return CARD_TILE.news;
  return { color: "var(--wofy-primary)", Icon: Radio };
}

export default function FeaturedPulseStrip() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);

  useEffect(() => {
    http.get("/pulse-items/featured", { params: { limit: 6 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % items.length), ADVANCE_MS);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items || items.length === 0) return null;

  const item = items[i];
  const tile = tileFor(item);
  const TileIcon = tile.Icon;

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="featured-pulse-strip">
      <div className="wofy-card !p-0 overflow-hidden grid md:grid-cols-[1.2fr,1fr]">
        {/* Left — image or fallback */}
        <Link to={`/pulse-item/${item.id}`} className="block relative aspect-video md:aspect-auto min-h-[220px]">
          {item.image_url ? (
            <img src={item.image_url} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${tile.color}22, ${tile.color}66)` }}
            >
              <TileIcon className="w-20 h-20" style={{ color: tile.color }} />
            </div>
          )}
          <div className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full text-white" style={{ background: "var(--wofy-primary)" }}>
            <Star className="w-3 h-3" style={{ fill: "#fff" }} /> Featured
          </div>
        </Link>

        {/* Right — text */}
        <div className="p-6 flex flex-col justify-between">
          <div>
            <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
              <Radio className="w-3 h-3 inline -mt-0.5 mr-1" /> NEIGHBORHOOD PULSE
            </div>
            <Link to={`/pulse-item/${item.id}`} data-testid={`featured-pulse-title-${item.id}`}>
              <h2 className="font-display text-3xl md:text-4xl leading-[1.1] hover:opacity-80">{item.title}</h2>
            </Link>
            {item.description && (
              <p className="mt-3 text-sm line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{item.description}</p>
            )}
            <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
              <Avatar src={item.user_avatar} name={item.user_name} size={24} />
              {item.user_handle ? (
                <Link to={`/u/${item.user_handle}`} className="hover:underline">{item.user_name || "Member"}</Link>
              ) : (
                <span>{item.user_name || "Member"}</span>
              )}
              {(item.city || item.country) && (
                <>
                  <span className="opacity-40">·</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[item.city, item.country].filter(Boolean).join(", ")}</span>
                </>
              )}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <Link to={`/pulse-item/${item.id}`} className="wofy-btn-primary text-sm" data-testid="featured-pulse-open-btn">
              Read this pulse <ArrowUpRight className="w-4 h-4" />
            </Link>
            {items.length > 1 && (
              <div className="flex items-center gap-1" data-testid="featured-pulse-nav">
                <button onClick={() => setI((n) => (n - 1 + items.length) % items.length)} className="p-1.5 rounded-full hover:bg-black/[0.05]" aria-label="Previous" data-testid="featured-pulse-prev">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex gap-1">
                  {items.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setI(idx)}
                      className="w-1.5 h-1.5 rounded-full transition-opacity"
                      style={{ background: "var(--wofy-primary)", opacity: idx === i ? 1 : 0.25 }}
                      aria-label={`Show pulse ${idx + 1}`}
                      data-testid={`featured-pulse-dot-${idx}`}
                    />
                  ))}
                </div>
                <button onClick={() => setI((n) => (n + 1) % items.length)} className="p-1.5 rounded-full hover:bg-black/[0.05]" aria-label="Next" data-testid="featured-pulse-next">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
